// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Button, Divider, Select } from "@/atoms"
import {
  BoldOutlined,
  CodeOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LineOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  OrderedListOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UnorderedListOutlined
} from "@/icons"
import { $isCodeNode, getDefaultCodeLanguage } from "@lexical/code-core"
import { $isLinkNode } from "@lexical/link"
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND
} from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode"
import {
  $createHeadingNode,
  $isHeadingNode,
  HeadingTagType
} from "@lexical/rich-text"
import {
  $isAtNodeEnd,
  $isParentElementRTL,
  $setBlocksType
} from "@lexical/selection"
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils"
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  SELECTION_CHANGE_COMMAND
} from "lexical"

import { useCallback, useEffect, useRef, useState } from "react"

import styles from "../RichTextEditor.module.scss"

const LowPriority = 1

function getSelectedNode(selection) {
  const anchor = selection.anchor
  const focus = selection.focus
  const anchorNode = selection.anchor.getNode()
  const focusNode = selection.focus.getNode()
  if (anchorNode === focusNode) {
    return anchorNode
  }
  const isBackward = selection.isBackward()
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode
  }
}

export default function ToolbarPlugin(props) {
  const { extraTools, disabled } = props
  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef(null)
  const [blockType, setBlockType] = useState("paragraph")
  const [, setSelectedElementKey] = useState(null)
  const [, setCodeLanguage] = useState("")
  const [, setIsRTL] = useState(false)
  const [, setIsLink] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isCode, setIsCode] = useState(false)
  const BLOCK_TYPE_OPTIONS = useRef()

  if (!BLOCK_TYPE_OPTIONS.current)
    BLOCK_TYPE_OPTIONS.current = [
      { value: "paragraph", label: <span>Normal</span> },
      { value: "h1", label: <h1>Heading 1</h1> },
      { value: "h2", label: <h2>Heading 2</h2> },
      { value: "h3", label: <h3>Heading 3</h3> },
      { value: "h4", label: <h4>Heading 4</h4> },
      { value: "h5", label: <h5>Heading 5</h5> },
      { value: "h6", label: <h6>Heading 6</h6> }
    ]

  const formatBlock = (type: string) => {
    editor.update(() => {
      const selection = $getSelection()

      if ($isRangeSelection(selection))
        if (type === "paragraph")
          $setBlocksType(selection, () => $createParagraphNode())
        else
          $setBlocksType(selection, () =>
            $createHeadingNode(type as HeadingTagType)
          )
    })
  }

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()
      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey)

        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode)
          const type = parentList ? parentList.getTag() : element.getTag()

          setBlockType(type)
        } else {
          let type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType()

          if (type === "root") type = "paragraph"

          setBlockType(type)

          if ($isCodeNode(element))
            setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage())
        }
      }

      setIsBold(selection.hasFormat("bold"))
      setIsItalic(selection.hasFormat("italic"))
      setIsUnderline(selection.hasFormat("underline"))
      setIsStrikethrough(selection.hasFormat("strikethrough"))
      setIsCode(selection.hasFormat("code"))
      setIsRTL($isParentElementRTL(selection))

      const node = getSelectedNode(selection)
      const parent = node.getParent()

      if ($isLinkNode(parent) || $isLinkNode(node)) setIsLink(true)
      else setIsLink(false)
    }
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(
          () => {
            updateToolbar()
          },
          { editor }
        )
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar()

          return false
        },
        LowPriority
      )
    )
  }, [editor, updateToolbar])

  const commonProps = { disabled, size: "small" }

  return (
    <div className="toolbar" ref={toolbarRef}>
      <div className={styles.buttonsContainerToolbar}>
        {extraTools && (
          <>
            <Select
              placeholder={<FontSizeOutlined />}
              rootClassName={styles.heading}
              labelRender={() => <FontSizeOutlined />}
              value={blockType}
              options={BLOCK_TYPE_OPTIONS.current}
              onChange={(value) => formatBlock(value)}
              size="small"
              disabled={disabled}
            />
            <Divider orientation="vertical" />
          </>
        )}
        <Button
          icon={<BoldOutlined />}
          ghost={isBold}
          type={isBold ? "primary" : "default"}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
          }}
          {...commonProps}
        />
        <Button
          icon={<ItalicOutlined />}
          ghost={isItalic}
          type={isItalic ? "primary" : "default"}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
          }}
          {...commonProps}
        />
        <Button
          icon={<UnderlineOutlined />}
          ghost={isUnderline}
          type={isUnderline ? "primary" : "default"}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
          }}
          {...commonProps}
        />
        <Button
          icon={<StrikethroughOutlined />}
          ghost={isStrikethrough}
          type={isStrikethrough ? "primary" : "default"}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
          }}
          {...commonProps}
        />
        <Button
          icon={<CodeOutlined />}
          ghost={isCode}
          type={isCode ? "primary" : "default"}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")
          }}
          {...commonProps}
        />
        {extraTools && (
          <Button
            icon={<LineOutlined />}
            type="default"
            onClick={() => {
              editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)
            }}
            {...commonProps}
          />
        )}
        <Divider orientation="vertical" />
        <Button
          icon={<OrderedListOutlined />}
          ghost={blockType === "ol"}
          type={blockType === "ol" ? "primary" : "default"}
          onClick={() => {
            if (blockType !== "ol") {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)
            } else {
              editor.dispatchCommand(REMOVE_LIST_COMMAND)
            }
          }}
          {...commonProps}
        />
        <Button
          icon={<UnorderedListOutlined />}
          ghost={blockType === "ul"}
          type={blockType === "ul" ? "primary" : "default"}
          onClick={() => {
            if (blockType !== "ul") {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)
            } else {
              editor.dispatchCommand(REMOVE_LIST_COMMAND)
            }
          }}
          {...commonProps}
        />
        <Divider orientation="vertical" />
        <Button
          icon={<MenuFoldOutlined />}
          type="default"
          onClick={() => {
            editor.dispatchCommand(OUTDENT_CONTENT_COMMAND)
          }}
          {...commonProps}
        />
        <Button
          icon={<MenuUnfoldOutlined />}
          type="default"
          onClick={() => {
            editor.dispatchCommand(INDENT_CONTENT_COMMAND)
          }}
          {...commonProps}
        />
        {extraTools}
      </div>
    </div>
  )
}
